/* Site-wide EN/FR language layer.
   Strategy: a master EN→FR dictionary (public/i18n/fr.json, lazy-loaded) applied
   two ways: (1) t() for programmatic strings, (2) a DOM translation engine that
   walks text nodes + common attributes so every page switches instantly without
   per-component refactors. Originals are stored on the node for clean restore. */
import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

const LanguageContext = createContext({ lang: 'en', setLang: () => {}, t: (s) => s, dict: null });
export const useLang = () => useContext(LanguageContext);

let _dict = null;
let _dictPromise = null;
const loadDict = () => {
  if (_dict) return Promise.resolve(_dict);
  if (!_dictPromise) {
    _dictPromise = fetch('/i18n/fr.json')
      .then((r) => r.json())
      .then((d) => { _dict = d; return d; })
      .catch((err) => {
        if (process.env.NODE_ENV !== 'production') console.warn('French dictionary failed to load; staying in English:', err);
        _dictPromise = null;
        return null;
      });
  }
  return _dictPromise;
};

const norm = (s) => s.replace(/\s+/g, ' ').trim();
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'CODE', 'PRE']);
const ATTRS = ['placeholder', 'title', 'aria-label', 'alt'];

class DomTranslator {
  constructor() { this.dict = null; this.observer = null; this.active = false; }

  translateNode(node) {
    const key = norm(node.nodeValue || '');
    if (!key) return;
    const fr = this.dict[key];
    if (fr && fr !== key) {
      if (node.__den === undefined) node.__den = node.nodeValue;
      const m = /^(\s*)[\s\S]*?(\s*)$/.exec(node.nodeValue);
      node.nodeValue = (m ? m[1] : '') + fr + (m ? m[2] : '');
    }
  }

  translateAttrs(el) {
    for (const a of ATTRS) {
      const v = el.getAttribute && el.getAttribute(a);
      if (!v) continue;
      const fr = this.dict[norm(v)];
      if (fr && fr !== v) {
        el.__denAttrs = el.__denAttrs || {};
        if (el.__denAttrs[a] === undefined) el.__denAttrs[a] = v;
        el.setAttribute(a, fr);
      }
    }
  }

  walk(root) {
    if (!root) return;
    if (root.nodeType === Node.TEXT_NODE) { this.translateNode(root); return; }
    if (root.nodeType !== Node.ELEMENT_NODE || SKIP_TAGS.has(root.tagName)) return;
    if (root.closest && root.closest('[data-no-translate]')) return;
    this.translateAttrs(root);
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, {
      acceptNode: (n) => {
        if (n.nodeType === Node.ELEMENT_NODE) {
          if (SKIP_TAGS.has(n.tagName) || (n.dataset && n.dataset.noTranslate !== undefined)) return NodeFilter.FILTER_REJECT;
          this.translateAttrs(n);
          return NodeFilter.FILTER_SKIP;
        }
        return NodeFilter.FILTER_ACCEPT;
      }
    });
    let n;
    while ((n = walker.nextNode())) this.translateNode(n);
  }

  apply(dict) {
    this.dict = dict;
    this.active = true;
    this.walk(document.body);
    if (!this.observer) {
      this.observer = new MutationObserver((muts) => {
        if (!this.active) return;
        for (const m of muts) {
          if (m.type === 'characterData') this.translateNode(m.target);
          else if (m.type === 'attributes' && m.target.nodeType === Node.ELEMENT_NODE) this.translateAttrs(m.target);
          else m.addedNodes && m.addedNodes.forEach((n) => this.walk(n));
        }
      });
    }
    this.observer.observe(document.body, {
      childList: true, subtree: true, characterData: true,
      attributes: true, attributeFilter: ATTRS
    });
  }

  restore() {
    this.active = false;
    if (this.observer) this.observer.disconnect();
    if (typeof document === 'undefined' || !document.body) return;
    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
    let n;
    while ((n = walker.nextNode())) {
      if (n.nodeType === Node.TEXT_NODE && n.__den !== undefined) {
        n.nodeValue = n.__den;
        delete n.__den;
      } else if (n.__denAttrs) {
        for (const [a, v] of Object.entries(n.__denAttrs)) n.setAttribute(a, v);
        delete n.__denAttrs;
      }
    }
  }
}

const translator = typeof window !== 'undefined' ? new DomTranslator() : null;

const initialLang = () => {
  try {
    const q = new URLSearchParams(window.location.search).get('lang');
    if (q === 'fr' || q === 'en') {
      try { localStorage.setItem('daakyi_lang', q); } catch { /* private mode */ }
      return q;
    }
  } catch { /* SSR / no window */ }
  try { return localStorage.getItem('daakyi_lang') || 'en'; } catch { return 'en'; }
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(initialLang);
  const [dict, setDict] = useState(_dict);

  useEffect(() => {
    document.documentElement.lang = lang;
    if (lang === 'fr') {
      loadDict().then((d) => {
        if (d) { setDict(d); translator && translator.apply(d); }
      });
    } else {
      translator && translator.restore();
    }
  }, [lang]);

  const setLang = useCallback((l) => {
    try { localStorage.setItem('daakyi_lang', l); } catch { /* private mode */ }
    setLangState(l);
  }, []);

  const t = useCallback((s) => (lang === 'fr' && dict && dict[s]) || s, [lang, dict]);

  const value = useMemo(() => ({ lang, setLang, t, dict }), [lang, setLang, t, dict]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};
